import { sidebarExpandedVar } from '@@partner:graphql/initial-data'
import { GQL_ID, NestedFilter } from '@@partner:types:graphql'
import { useReactiveVar } from '@apollo/client'
import React, { useState, useEffect, useCallback } from 'react'
import { Button, Checkbox, Col, FlexboxGrid, Panel, SelectPicker } from 'rsuite'
import { notifyOk } from '@@partner:helpers/notification'
import { useFetchAllProductGroups } from '@@partner:graphql/query/productGroup/allProductGroups.gql'
import { useFetchAllMarketingLabels } from '@@partner:graphql/query/labels/allMarketingLabels.gql'
import { useCategoriesFetcher } from '@@partner:graphql/query/catalog/categoriesWithParent.gql'
import ProductGroupSelector from '../form/group/ProductGroupSelector'
import ProductStatus from '../form/info/ProductStatus'
import { commonFieldChange } from '@@partner:helpers/util'
import { ProductInfoInput, useMutateBulkUpdateProducts } from '@@partner:graphql/mutation/catalog/bulkUpdateProducts'
import { InputItemDataType } from 'rsuite/esm/InputPicker'
import { Category, RsuiteItemDataType } from '@@partner:types:app'
import CategoriesFilter from './CategoriesFilter'

type props = {
  show: boolean,
  filter?: NestedFilter
  selectedIds: GQL_ID[]
}

type State = ProductInfoInput

export default function ProductBulkUpdatePanel ({
  show,
  filter,
  selectedIds
}: props) {
  const [ state, setState ] = useState<State>({})
  const [ categoryData, setCategoryData ] = useState<InputItemDataType[]>([])

  const [ selectedGroup, setSelectedGroup ] = useState<string>()

  const handleFieldChange = commonFieldChange(setState)

  const [ bulkUpdate, { loading } ] = useMutateBulkUpdateProducts()
  const { data: labelsData } = useFetchAllMarketingLabels({
    pager: { page: 1, limit: 100 }
  })
  const { fetchCategories, fetchRootCategories } = useCategoriesFetcher()
  const mapToItemDataType = useCallback((category: Category) => ({
    label: category.name,
    value: category.id,
    children: [],
    loading: false,
    parent_id: category.parent_id
  }), [])
  useEffect(() => {
    const loadCategories = async () => {
      const initial = await fetchRootCategories({})
      setCategoryData(initial.categories.edges.map(mapToItemDataType))
    }
    if (show) {
      loadCategories()
    }
  }, [ show, fetchRootCategories, mapToItemDataType ])

  const handleGetChildren = useCallback(async (node: RsuiteItemDataType) => {
    if (node.value) {
      const data = await fetchCategories({
        parentId: node.value
      })
      return data.categories.edges.map(mapToItemDataType)
    }
    return []
  }, [ fetchCategories, mapToItemDataType ])
  const labelsOptions = labelsData?.allMarketingLabels?.edges?.map(label => ({
    label: label.name,
    value: label.id
  })) || []

  const handleChangeProducts = async () => {
    if (!loading) {
      const inputFilter = filter ? filter : {
        fields: [ 'id' ],
        op: 'contains',
        value: selectedIds.map(id => id.toString())
      }
      const productInfo = Object.fromEntries(
        Object.entries(state).filter(([ _, v ]) => v != null)
      )
      await bulkUpdate({
        variables: {
          productInfo,
          filter: inputFilter
        }
      })
      notifyOk('Продукты успешно обновлены')
    }
  }

  const handleCategoryChange = (value: GQL_ID[] | GQL_ID | null) => {
    const newValue = Array.isArray(value) ? value[0] : value
    setState(prev => ({ ...prev, productCategory: newValue || undefined }))
  }

  if (!show) {
    return null
  }
  return (
    <Panel 
      shaded
      className="my-4"
      style={{
        backgroundColor: 'white',
        padding: '10px'
      }}
    >
      <FlexboxGrid justify="space-between" align="middle">
        <FlexboxGrid.Item>
          <FlexboxGrid>
            <Col>
              <p>Статус</p>
              <ProductStatus
                placement='bottomStart'
                value={state.status ?? undefined}
                onChange={handleFieldChange('status')}
                cleanable
              />
            </Col>
            <Col>
              <p>Группа</p>
              <FlexboxGrid align="middle">
                <ProductGroupSelector
                  placement='bottomStart'
                  multiple={false}
                  value={state.productGroup ? `${state.productGroup}` : undefined}
                  onChange={handleFieldChange('productGroup')}
                  placeholder="Выбрать группу"
                />
                <Checkbox
                  checked={state.replaceProductGroup}
                  onChange={(_, checked) => setState(prev => ({ ...prev, replaceProductGroup: checked }))}
                >
                  Заменить
                </Checkbox>
              </FlexboxGrid>
            </Col>
            <Col>
              <p>Категория</p>
              <CategoriesFilter
                value={state.productCategory ? [ state.productCategory ] : []}
                onChange={handleCategoryChange}
                multiple={false}
              />
            </Col>
            <Col>
              <p>Лейбл</p>
              <FlexboxGrid align="middle">
                <SelectPicker
                  placement='bottomStart'
                  data={labelsOptions}
                  value={state.productMarketingLabel}
                  onChange={handleFieldChange('productMarketingLabel')}
                  placeholder="Выбрать лейбл"
                  cleanable
                />
                <Checkbox
                  checked={state.replaceProductMarketingLabel}
                  onChange={(_, checked) => setState(prev => ({ ...prev, replaceProductMarketingLabel: checked }))}
                >
                  Заменить
                </Checkbox>
              </FlexboxGrid>
            </Col>
          </FlexboxGrid>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item>
          <Button 
            size="lg" 
            color="green" 
            appearance="primary"
            onClick={handleChangeProducts}
            loading={loading}
          >
            Применить
          </Button>
        </FlexboxGrid.Item>
      </FlexboxGrid>
    </Panel>
  )
}